import { ArrowRight, Building2, Calendar, FileText, Search, Users } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { cx } from "../lib/cx"
import type { Invoice } from "../types"

type SearchResult = {
  id: string
  label: string
  type: "client" | "project" | "invoice" | "contractor" | "task" | "time"
  href: string
  subtitle?: string
  icon: typeof Search
}

type SearchModalProps = {
  isOpen: boolean
  onClose: () => void
}

const SearchModal = ({ isOpen, onClose }: SearchModalProps) => {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState("")
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [clients, setClients] = useState<Array<{ id: string; name: string; company?: string }>>([])
  const [projects, setProjects] = useState<Array<{ id: string; name: string; client?: string }>>([])
  const [tasks, setTasks] = useState<Array<{ id: string; title: string; dueDate?: string }>>([])
  const [timeEntries, setTimeEntries] = useState<Array<{ contractor: string }>>([])

  useEffect(() => {
    if (isOpen) {
      Promise.all([
        fetch('/api/invoices').then(r => r.json()),
        fetch('/api/clients').then(r => r.json()),
        fetch('/api/projects').then(r => r.json()),
        fetch('/api/tasks').then(r => r.json()),
        fetch('/api/time-entries').then(r => r.json()),
      ]).then(([invData, cliData, projData, taskData, timeData]) => {
        if (invData.invoices) setInvoices(invData.invoices)
        if (cliData.clients) setClients(cliData.clients)
        if (projData.projects) setProjects(projData.projects)
        if (taskData.tasks) setTasks(taskData.tasks)
        if (timeData.timeEntries) setTimeEntries(timeData.timeEntries)
      }).catch(err => console.error("Failed to fetch search data", err))
    }
  }, [isOpen])

  // Build comprehensive search index
  const searchIndex = useMemo<SearchResult[]>(() => {
    const results: SearchResult[] = []

    // Clients
    clients.forEach((client) => {
      results.push({
        id: `client-${client.id}`,
        label: client.name,
        type: "client",
        href: `/clients?client=${client.id}`,
        subtitle: client.company,
        icon: Building2,
      })
    })

    // Projects
    projects.forEach((project) => {
      results.push({
        id: `project-${project.id}`,
        label: project.name,
        type: "project",
        href: `/projects/${project.id}`,
        subtitle: project.client,
        icon: FileText,
      })
    })

    // Invoices
    invoices.forEach((invoice) => {
      results.push({
        id: `invoice-${invoice.id}`,
        label: invoice.id,
        type: "invoice",
        href: `/billing?invoice=${invoice.id}`,
        subtitle: `${invoice.client} · ${invoice.amount}`,
        icon: FileText,
      })
    })

    // Contractors (from time entries)
    const contractors = Array.from(new Set(timeEntries.map((e) => e.contractor)))
    contractors.forEach((contractor) => {
      results.push({
        id: `contractor-${contractor}`,
        label: contractor,
        type: "contractor",
        href: `/time?contractor=${encodeURIComponent(contractor)}`,
        subtitle: "Contractor",
        icon: Users,
      })
    })

    // Tasks
    tasks.forEach((task) => {
      results.push({
        id: `task-${task.id}`,
        label: task.title,
        type: "task",
        href: `/tasks?taskId=${encodeURIComponent(task.id)}`,
        subtitle: task.dueDate ? `Due: ${task.dueDate}` : undefined,
        icon: Calendar,
      })
    })

    return results
  }, [clients, projects, invoices, timeEntries, tasks])

  const filteredResults = useMemo(() => {
    if (!query.trim()) {
      // Show recent/popular items when no query
      return searchIndex.slice(0, 8)
    }

    const lowered = query.toLowerCase()
    return searchIndex
      .filter((item) => {
        const matchesLabel = item.label.toLowerCase().includes(lowered)
        const matchesSubtitle = item.subtitle?.toLowerCase().includes(lowered)
        return matchesLabel || matchesSubtitle
      })
      .slice(0, 10)
  }, [query, searchIndex])

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
    }
  }, [isOpen])

  const closeModal = useCallback(() => {
    setQuery("")
    setSelectedIndex(0)
    onClose()
  }, [onClose])

  const handleSelect = useCallback((result: SearchResult) => {
    navigate(result.href)
    closeModal()
  }, [closeModal, navigate])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return

      if (event.key === "Escape") {
        closeModal()
      } else if (event.key === "ArrowDown") {
        event.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, filteredResults.length - 1))
      } else if (event.key === "ArrowUp") {
        event.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (event.key === "Enter" && filteredResults[selectedIndex]) {
        handleSelect(filteredResults[selectedIndex])
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, filteredResults, selectedIndex, closeModal, handleSelect])

  const getTypeLabel = (type: SearchResult["type"]) => {
    const labels: Record<SearchResult["type"], string> = {
      client: "Client",
      project: "Project",
      invoice: "Invoice",
      contractor: "Contractor",
      task: "Task",
      time: "Time Entry",
    }
    return labels[type]
  }

  const getTypeColor = (type: SearchResult["type"]) => {
    const colors: Record<SearchResult["type"], string> = {
      client: "bg-blue-100 text-blue-700",
      project: "bg-purple-100 text-purple-700",
      invoice: "bg-green-100 text-green-700",
      contractor: "bg-orange-100 text-orange-700",
      task: "bg-yellow-100 text-yellow-700",
      time: "bg-indigo-100 text-indigo-700",
    }
    return colors[type]
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]"
      onClick={closeModal}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl rounded-[24px] border border-line bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-line p-4">
          <Search className="h-5 w-5 text-stone" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSelectedIndex(0)
            }}
            placeholder="Search clients, projects, invoices, contractors, tasks..."
            className="flex-1 bg-transparent text-base text-slate-900 outline-none placeholder:text-stone"
          />
          <kbd className="hidden rounded border border-line bg-slate-50 px-2 py-1 text-xs font-mono text-stone sm:inline">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {filteredResults.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-stone">No results found</p>
              <p className="mt-1 text-xs text-stone">Try a different search term</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredResults.map((result, index) => {
                const Icon = result.icon
                const isSelected = index === selectedIndex

                return (
                  <button
                    key={result.id}
                    type="button"
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={cx(
                      "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition",
                      isSelected ? "bg-slate-100" : "hover:bg-slate-50",
                    )}
                  >
                    <div
                      className={cx(
                        "flex h-8 w-8 items-center justify-center rounded-lg",
                        getTypeColor(result.type),
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{result.label}</p>
                      {result.subtitle && (
                        <p className="truncate text-xs text-stone">{result.subtitle}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={cx(
                          "rounded-full px-2 py-0.5 text-xs font-medium",
                          getTypeColor(result.type),
                        )}
                      >
                        {getTypeLabel(result.type)}
                      </span>
                      {isSelected && (
                        <ArrowRight className="h-4 w-4 text-stone" />
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer hints */}
        <div className="border-t border-line px-4 py-2">
          <div className="flex items-center justify-between text-xs text-stone">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-line bg-slate-50 px-1.5 py-0.5 font-mono">
                  ↑
                </kbd>
                <kbd className="rounded border border-line bg-slate-50 px-1.5 py-0.5 font-mono">
                  ↓
                </kbd>
                <span>Navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-line bg-slate-50 px-1.5 py-0.5 font-mono">
                  Enter
                </kbd>
                <span>Select</span>
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-line bg-slate-50 px-1.5 py-0.5 font-mono">
                ESC
              </kbd>
              <span>Close</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchModal
